<script setup lang="ts">
import { corteLigado, funilVerPlanos } from "@/utils/eventosDoFunil";
// Tela única DESKTOP: três cards (idênticos ao mobile) lado a lado na ordem
// Gold → Plus → Basic, reusando AssinePlanCardPeriodMobile. O texto de chamada
// fica à esquerda dos cards (visitante). A comparação de benefícios e o rodapé
// permanecem abaixo. Cada card tem seu próprio botão de avanço.
import translaters from "@/tradutors/assine";
import translatersTitle from "@/tradutors/assineTitleUpgrade";
import AssineTitleUpgrade from "@/components/Assine/AssineTitleUpgrade.vue";
import AssinePlanCardPeriodMobile from "@/components/Assine/AssinePlanCardPeriodMobile.vue";
import { formatMoney } from "@/shared/utils/money";
import { comHorasDaLive } from "@/utils/horasDaLive";

const storePlan = planStore();
const storeAssineChoice = assineChoiceStore();
const storeUser = userStore();
const route = useRoute();

const isActiveUser = computed(() => storeUser.isActiveUser);
const isLoggedIn = computed(() => storeUser.isLoggedIn);
const dailylimit = computed(() => storePlan.effectiveDownloadLimit);
const reactivePlans = computed(() => storePlan.plansType);
const reactiveLocale = computed(() => route.params.language);

const props = defineProps({
  // Pedido atual; pode ser {} até a API responder (tratado como não-ativo).
  order: {
    type: [Object, Boolean],
    required: false,
    default: () => ({}),
  },
});

// Regras centrais (compartilhadas com o mobile): aqui usamos apenas o topState
// (cabeçalho de topo) e isAdminUser. Cada card calcula o resto internamente.
const { topState, isAdminUser } = useAssinePlanRules({
  order: computed(() => props.order),
  isLoggedIn,
  isActiveUser,
  saldoUpgrade: computed(() => storePlan.saldoUpgrade),
});

const translate = computed(() => {
  if (reactiveLocale.value === "pt-br") {
    return translaters.portugues;
  }
  return translaters.ingles;
});

const translateTitle = computed(() => {
  if (reactiveLocale.value === "pt-br") {
    return translatersTitle.portugues;
  }
  return translatersTitle.ingles;
});

// Desktop: ordem Gold → Plus → Basic (inverso do ORDER_BASIC_FIRST do mobile).
const ORDER_GOLD_FIRST: Record<string, number> = { Gold: 0, Plus: 1, Basic: 2 };
const plansOrderedGoldFirst = computed(() =>
  [...reactivePlans.value].sort(
    (a: any, b: any) =>
      (ORDER_GOLD_FIRST[a.plan_type_name ?? ""] ?? 99) -
      (ORDER_GOLD_FIRST[b.plan_type_name ?? ""] ?? 99)
  )
);

// Coluna à esquerda dos cards para quem NÃO é assinante ativo:
//  - VISITANTE (não logado): texto de chamada de marketing ("Tenha acesso a…").
//  - LOGADO SEM assinatura ativa: mensagem de "recupere seu acesso".
// Admin fica de fora (seu aviso é no topo da página, em index.vue).
const showLeftIntro = computed(
  () => topState.value === "guest" && !isAdminUser.value
);
const showLeftLoggedInactive = computed(
  () => topState.value === "logged-not-active" && !isAdminUser.value
);

// Assinante ATIVO (Gold/Plus/Basic): as mensagens de topo (título + saldo) migram
// para a coluna à esquerda dos cards, na "coluna vazia" ao lado do Gold (desktop
// >=1200px). No tablet (<1200px) voltam para cima do grid, em largura total.
// Admin não entra (seu aviso fica no topo da página, em index.vue).
const showLeftActiveHeader = computed(
  () => isActiveUser.value && !isAdminUser.value
);

// ----- GTM -----
function eventGTMViewCart() {
  // Depois do corte da medição: view_item_list no lugar do view_cart.
  if (corteLigado()) {
    funilVerPlanos("planos", route.query.acionador);
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "view_cart",
    cart: {
      tipo: "assinatura",
    },
  });
}

function eventGTMBeginCheckout(name: string) {
  // Depois do corte, begin_checkout é a abertura do pagamento, não a grade de planos.
  if (corteLigado()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "begin_checkout",
    begin_checkout: {
      tipo:
        storeAssineChoice.choiceType !== "credits" ? "assinatura" : "credito",
      acionador: route.query.acionador ?? "link direto",
      currency: reactiveLocale.value === "pt-br" ? "BRL" : "USD",
      plano: name || "Plus",
    },
  });
}

onMounted(() => {
  eventGTMViewCart();
  eventGTMBeginCheckout("Plus");
});

function currencyPrefix(): string {
  // PT-BR: sem prefixo "R$" (mostra só o valor). EN: mantém "$".
  return reactiveLocale.value === "pt-br" ? "" : "$";
}

// Coluna Plus com fundo cinza SOMENTE quando NÃO ativo (usado pelas marcas da
// tabela de benefícios para manter o realce da coluna Plus).
const plusColumnGray = computed(() => !isActiveUser.value);

// Colunas da tabela de benefícios (ordem Gold → Plus → Basic).
const BENEFIT_COLUMNS = ["Gold", "Plus", "Basic"] as const;

// ----- Benefícios desktop (lista nova V2; exclusivos em negrito) -----
// Uma linha por benefício: nome (1ª coluna) + acesso por plano (check/X).
// Negrito nos itens exclusivos (gold/plus), que na lista vêm sempre primeiro.
// {horasPlus}/{horasGold} viram as horas de live do admin ("Tempo de acesso").
const horasDaLive = useHorasDaLive();
const benefitRows = computed(() => {
  const list = (translate.value as any).planBenefitsDesktopV2 as Array<{
    text: string;
    accessLevel: "gold" | "plus" | "all";
  }>;
  return list.map((benefit) => ({
    text: comHorasDaLive(benefit.text, horasDaLive.value),
    bold: benefit.accessLevel !== "all",
    Gold: true,
    Plus: benefit.accessLevel !== "gold",
    Basic: benefit.accessLevel === "all",
  }));
});

// Topo: estados Gold têm textos próprios; demais usam AssineTitleUpgrade.
const showGoldTopState = computed(
  () => topState.value === "gold-gt-30" || topState.value === "gold-le-30"
);

// Saldo real do assinante formatado (PT: "320,00" / EN: "$ 320,00"), para a
// mensagem de upgrade/renovação do ativo NÃO-Gold (topState === 'active-upgrade').
const saldoFormatted = computed(
  () =>
    `${currencyPrefix()} ${formatMoney(storePlan.saldoUpgrade, {
      alwaysCents: true,
    })}`
);

// Data de validade da assinatura atual (order.finish), para a mensagem Gold >30 dias.
const orderFinish = computed(() => {
  const o = props.order;
  return o && typeof o === "object" ? (o as any).finish ?? "" : "";
});

// Nome do tipo de plano atual do assinante (ex.: "Basic"/"Plus"), anexado ao
// título "Melhore o seu plano <Plano>" no estado active-upgrade.
const currentPlanTypeName = computed(() => {
  const o = props.order;
  return o && typeof o === "object" ? (o as any).plan_type_name ?? "" : "";
});

const svgCheck =
  '<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15px" height="15px" viewBox="0,0,256,256"style="fill:#40C057;"><g fill="#40c057" fill-rule="nonzero" stroke="#40c057" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10" stroke-dasharray="" stroke-dashoffset="0" font-family="none" font-weight="none" font-size="none" text-anchor="none" style="mix-blend-mode: normal"><g transform="scale(5.12,5.12)"><path d="M41.9375,8.625c-0.66406,0.02344 -1.27344,0.375 -1.625,0.9375l-18.8125,28.78125l-12.1875,-10.53125c-0.52344,-0.54297 -1.30859,-0.74609 -2.03125,-0.51953c-0.71875,0.22266 -1.25391,0.83203 -1.37891,1.57422c-0.125,0.74609 0.17578,1.49609 0.78516,1.94531l13.9375,12.0625c0.4375,0.37109 1.01563,0.53516 1.58203,0.45313c0.57031,-0.08594 1.07422,-0.41016 1.38672,-0.89062l20.09375,-30.6875c0.42969,-0.62891 0.46484,-1.44141 0.09375,-2.10547c-0.37109,-0.66016 -1.08594,-1.05469 -1.84375,-1.01953z"></path></g></g></svg>';
// X recolorido para #b20000 (vermelho) na lista V2 do desktop.
const svgCancel =
  '<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="15px" height="15px" viewBox="0,0,256,256"style="fill:#b20000;"><g fill="#b20000" fill-rule="nonzero" stroke="none" stroke-width="1" stroke-linecap="butt" stroke-linejoin="miter" stroke-miterlimit="10" stroke-dasharray="" stroke-dashoffset="0" font-family="none" font-weight="none" font-size="none" text-anchor="none" style="mix-blend-mode: normal"><g transform="scale(5.12,5.12)"><path d="M9.15625,6.3125l-2.84375,2.84375l15.84375,15.84375l-15.9375,15.96875l2.8125,2.8125l15.96875,-15.9375l15.9375,15.9375l2.84375,-2.84375l-15.9375,-15.9375l15.84375,-15.84375l-2.84375,-2.84375l-15.84375,15.84375z"></path></g></g></svg>';
</script>

<template>
  <div class="container">
    <!-- Admin (level 1/4): o aviso fica no topo da página (assine/index.vue);
         aqui não renderizamos cabeçalho próprio para não conflitar com ele.
         ASSINANTE ATIVO (Gold/Plus/Basic): as mensagens de topo (título + saldo)
         migram para a coluna à esquerda dos cards (ver .desktop-intro--active
         abaixo); aqui resta só o fallback de visitante/logado-inativo com saldo. -->
    <template v-if="!isAdminUser && !showLeftActiveHeader">
      <AssineTitleUpgrade :order="props.order" />
    </template>

    <!-- DESKTOP: coluna de texto (esquerda, só visitante) + 3 cards lado a lado
         na ordem Gold → Plus → Basic, reusando o card do mobile. A 1ª célula
         (240px) é sempre renderizada para manter os cards alinhados às colunas
         de benefícios; só recebe o texto quando visitante. -->
    <div
      class="desktop-plans-row mt-5"
      :class="{ 'desktop-plans-row--active-header': showLeftActiveHeader }"
    >
      <!-- Coluna à esquerda dos cards. Conteúdo alterna por estado do usuário:
           - Visitante/logado-inativo: texto de chamada (migrado do header de
             index.vue), quebrado em 5 trechos p/ font-sizes distintos.
           - Assinante ativo: as mensagens de topo (título + saldo) migradas para cá. -->
      <div
        class="desktop-intro"
        :class="{
          'desktop-intro--en': reactiveLocale !== 'pt-br',
          'desktop-intro--active': showLeftActiveHeader,
          'desktop-intro--inactive': showLeftLoggedInactive,
        }"
      >
        <!-- Visitante (não logado): texto de chamada de marketing. -->
        <template v-if="showLeftIntro">
          <div class="desktop-intro-1">{{ translate.assineIntroDesktop1 }}</div>
          <div class="desktop-intro-2">{{ translate.assineIntroDesktop2 }}</div>
          <div class="desktop-intro-3">{{ translate.assineIntroDesktop3 }}</div>
          <div class="desktop-intro-4">{{ translate.assineIntroDesktop4 }}</div>
          <div class="desktop-intro-5">{{ translate.assineIntroDesktop5 }}</div>
        </template>

        <!-- Logado SEM assinatura ativa: título (cinza) + chamada de recuperação
             (laranja), no lugar do texto de marketing do visitante. -->
        <template v-else-if="showLeftLoggedInactive">
          <div class="desktop-intro-inactive-title">
            {{ translate.assineInactiveTitle }}
          </div>
          <div class="desktop-intro-inactive-sub">
            {{ translate.assineInactiveSub }}
          </div>
        </template>

        <!-- Assinante ativo: mesmo conteúdo/estilos do antigo cabeçalho de topo
             (estados Gold com textos próprios; demais = título + saldo real). -->
        <template v-else-if="showLeftActiveHeader">
          <template v-if="showGoldTopState">
            <!-- Gold >30 dias (upgrade bloqueado): parabéns + validade. -->
            <template v-if="topState === 'gold-gt-30'">
              <div class="gold-congrats-title">{{ translateTitle.goldGt30Title }}</div>
              <div class="gold-congrats-subtitle">
                {{ translateTitle.goldGt30Validity }} {{ orderFinish }}
              </div>
            </template>
            <!-- Gold <=30 dias (renovação liberada): título + saldo + validade. -->
            <template v-else>
              <div class="gold-renew-title">{{ translateTitle.goldLe30Title }}</div>
              <div class="gold-renew-subtitle">
                {{ translateTitle.goldLe30Prefix }}
                <strong>{{ translateTitle.goldLe30BalanceLabel }} {{ saldoFormatted }}</strong>
                {{ translateTitle.goldLe30Middle }} {{ orderFinish }}
              </div>
            </template>
          </template>
          <!-- Assinante ativo NÃO-Gold (Plus/Basic): título + saldo REAL. -->
          <template v-else>
            <div class="gold-top-title">
              {{ translateTitle.title }} {{ currentPlanTypeName }}
            </div>
            <div class="active-upgrade-subtitle">
              {{ translateTitle.activeUpgradePrefix }}
              <strong>{{ translateTitle.activeUpgradeBalanceLabel }} {{ saldoFormatted }}</strong>
              {{ translateTitle.activeUpgradeSuffix }}
            </div>
          </template>
        </template>
      </div>

      <!-- Linha de cards (cada card tem seu próprio botão de avanço). -->
      <div class="desktop-cards">
        <div
          v-for="plan in plansOrderedGoldFirst"
          :key="`card-${plan.plan_type_id}`"
          class="desktop-card-col"
        >
          <AssinePlanCardPeriodMobile
            :planType="plan.plan_type_id"
            :order="props.order ?? {}"
            :showBenefits="false"
          />
        </div>
      </div>
    </div>

    <!-- Benefícios (planBenefitsDesktopV2): nome só na 1ª coluna (alinhado à
         esquerda); nas colunas dos planos, apenas o check/X centralizado.
         O nome usa v-html para permitir <br> de quebra manual no tradutor. -->
    <div class="grid-benefits">
      <template v-for="(benefit, i) in benefitRows" :key="`benefit-${i}`">
        <span
          class="benefit-name"
          :class="{ 'benefit-name-bold': benefit.bold }"
          v-html="benefit.text"
        ></span>
        <div
          v-for="typeName in BENEFIT_COLUMNS"
          :key="`benefit-${i}-${typeName}`"
          class="benefit-mark"
          :class="{
            'plus-gray': typeName === 'Plus' && plusColumnGray,
            'benefit-mark--off': !(benefit as any)[typeName],
          }"
          v-html="(benefit as any)[typeName] ? svgCheck : svgCancel"
        ></div>
      </template>
    </div>

    <!-- Rodapé: assinatura sigilosa + limite de downloads diário. -->
    <div class="grid-footer text-center">
      <div class="footer-secrecy" v-html="translate.secrecyFooter"></div>
      <div class="footer-download">
        {{ translate.footerDownloadLimit1 }}
        {{ dailylimit }}
        {{ translate.footerDownloadLimit2 }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="css">
/* Cabeçalho de estados Gold (topo). */
.gold-top-title {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 50px;
  line-height: 1.1;
  color: #585858;
  text-align: center;
}
.gold-top-subtitle {
  font-family: "Roboto-Regular";
  font-weight: 700;
  font-size: 25px;
  line-height: 35px;
  color: #ff6000;
  text-align: center;
  margin-top: 10px;
}
.gold-top-text {
  font-family: "Exo-2";
  font-size: 15px;
  line-height: 20px;
  color: #5b5858;
  text-align: center;
  margin-top: 6px;
}

/* Gold >30 dias: título de parabéns (laranja) + validade da assinatura (cinza). */
.gold-congrats-title {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 40px;
  line-height: 1.1;
  color: #ff6000;
  text-align: center;
}
.gold-congrats-subtitle {
  font-family: "Exo-2";
  font-weight: 400;
  font-size: 20px;
  line-height: 28px;
  color: #5b5858;
  text-align: center;
  margin-top: 8px;
}

/* Gold <=30 dias: título de renovação (laranja) + saldo/validade (cinza, saldo em negrito). */
.gold-renew-title {
  font-family: "Exo-2";
  font-weight: 700;
  font-size: 50px;
  line-height: 1.1;
  color: #ff6000;
  text-align: center;
}
.gold-renew-subtitle {
  font-family: "Exo-2";
  font-weight: 400;
  font-size: 20px;
  line-height: 28px;
  color: #5b5858;
  text-align: center;
  margin-top: 8px;
}
.gold-renew-subtitle strong {
  font-weight: 700;
}

/* Assinante ativo NÃO-Gold: subtítulo (laranja) com o saldo real em destaque. */
.active-upgrade-subtitle {
  font-family: "Roboto-Regular";
  font-weight: 400;
  font-size: 25px;
  line-height: 35px;
  color: #ff6000;
  text-align: center;
  margin-top: 10px;
}
.active-upgrade-subtitle strong {
  font-weight: 700;
}

/* DESKTOP: texto de chamada à esquerda + 3 cards lado a lado.
   MESMO grid da tabela de benefícios (240px + 3×1fr, gap 0) para que o CENTRO de
   cada card fique alinhado verticalmente ao check/X do plano correspondente. O
   gap 0 (sem espaçamentos entre cards) também alarga cada card, evitando que o
   rádio quebre de linha. A 1ª célula (240px) é o texto de chamada (vazia quando
   ativo). */
.desktop-plans-row {
  display: grid;
  grid-template-columns: 240px 1fr;
  align-items: center; /* centraliza o texto verticalmente em relação aos cards */
  gap: 0;
  width: 100%;
}

/* Coluna de texto à esquerda (só visitante). Fonte fluida (clamp) para preencher
   a largura da coluna de ponta a ponta. */
.desktop-intro {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-right: 16px;
}

/* Cabeçalho do assinante ativo na coluna à esquerda dos cards: centralizado.
   Reaproveita os mesmos classes/estilos do antigo cabeçalho de topo (definidos
   mais acima). A partir de 1200px reduzimos as fontes para caber nos 240px da
   coluna (ver @media min-width:1200px). Abaixo de 1200px o bloco volta ao topo,
   em largura total, com os tamanhos originais (ver @media max-width:1199px). */
.desktop-intro--active {
  align-items: center;
  text-align: center;
  padding-right: 0;
}

/* Logado SEM assinatura ativa: título (cinza) + chamada (laranja), centralizado.
   Ocupa a mesma célula do texto de visitante e herda o ocultar-no-tablet
   (<1200px) — não leva a classe --active, então cai no :not(.desktop-intro--active). */
.desktop-intro--inactive {
  align-items: center;
  text-align: center;
  padding-right: 0;
}
.desktop-intro-inactive-title {
  font-family: "Roboto-Medium";
  font-weight: 900;
  font-size: clamp(22px, 2vw, 28px);
  line-height: 1.15;
  letter-spacing: 0.5px;
  color: #585858;
}
.desktop-intro-inactive-sub {
  font-family: "Roboto-Medium";
  font-weight: 500;
  font-size: clamp(16px, 1.6vw, 21px);
  line-height: 1.3;
  color: #ff6000;
  margin-top: 16px;
}

/* Desktop largo (>=1200px): fontes reduzidas para o cabeçalho caber na coluna
   estreita ao lado do card Gold, mantendo a hierarquia (título / saldo). */
@media screen and (min-width: 1200px) {
  .desktop-intro--active .gold-top-title,
  .desktop-intro--active .gold-renew-title,
  .desktop-intro--active .gold-congrats-title {
    font-size: 30px;
    line-height: 1.15;
  }
  .desktop-intro--active .active-upgrade-subtitle {
    font-size: 20px;
    line-height: 1.3;
    margin-top: 12px;
  }
  .desktop-intro--active .gold-renew-subtitle,
  .desktop-intro--active .gold-congrats-subtitle {
    font-size: 17px;
    line-height: 1.35;
    margin-top: 10px;
  }
}
/* Trechos 1-3 (laranja) e 4-5 (preto, sublinhado). Cada trecho tem seu próprio
   font-size para o ajuste tipográfico pedido. Base comum + tamanhos por trecho. */
.desktop-intro-1,
.desktop-intro-2,
.desktop-intro-3,
.desktop-intro-4,
.desktop-intro-5 {
  font-family: "Roboto-Medium";
  font-weight: 900;
  letter-spacing: 1px;
  line-height: 1.05;
  text-align: left;
}
.desktop-intro-1,
.desktop-intro-2,
.desktop-intro-3 {
  color: #ff6000;
}
.desktop-intro-4,
.desktop-intro-5 {
  color: #000000;
}

/* Font-sizes por trecho (ajuste fino tipográfico — calibrável). */
.desktop-intro-1 {
  font-size: clamp(20px, 1.9vw, 30px);
  line-height: 1.5;
}
.desktop-intro-2 {
  font-size: clamp(22px, 2.1vw, 22.7px);
  line-height: 1.5;
}
.desktop-intro-3 {
  font-size: clamp(22px, 2.1vw, 25px);
  line-height: 1.5;
}
.desktop-intro-4 {
  margin-top: 5px;
  font-size: clamp(20px, 1.9vw, 34.4px);
}
.desktop-intro-5 {
  font-size: clamp(24px, 2.3vw, 42px);
  margin-top: 10px;
}

/* Inglês: font-sizes próprios por trecho (base = valores PT, calibrável). */
.desktop-intro--en .desktop-intro-1 {
  font-size: clamp(20px, 1.9vw, 34px);
}
.desktop-intro--en .desktop-intro-2 {
  font-size: 18px;
}
.desktop-intro--en .desktop-intro-3 {
  font-size: clamp(22px, 2.1vw, 28px);
}
.desktop-intro--en .desktop-intro-4 {
  font-size: 48px;
}
.desktop-intro--en .desktop-intro-5 {
  font-size: 47px;
}

/* As 3 colunas de cards = as 3 colunas de plano da tabela de benefícios (mesma
   região 1fr dividida em 3, gap 0), garantindo alinhamento card ↔ check/X. */
.desktop-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  align-items: stretch;
}
.desktop-card-col {
  min-width: 0; /* permite encolher sem estourar a linha */
  /* Respiro entre cards SEM deslocar o centro da coluna (mantém o alinhamento). */
  padding: 0 6px;
  display: flex;
}

/* O card reusado (mobile) tem .container (Bootstrap) como raiz: neutralizar
   max-width/padding/margin para os 3 caberem lado a lado. :deep porque o card é
   um componente filho com CSS scoped. */
.desktop-card-col :deep(.container) {
  max-width: 100% !important;
  width: 100%;
  padding-left: 0;
  padding-right: 0;
  margin: 0;
  /* Preenche a altura da coluna (esticada pelo grid) para que o card filho possa
     crescer e os 3 cards fiquem da MESMA altura (como no mobile). */
  height: 100%;
  display: flex;
  flex-direction: column;
}
.desktop-card-col :deep(.card) {
  margin-top: 20px;
  width: 100%;
  /* Card cresce para preencher a coluna: o Gold (1 período) iguala a altura de
     Plus/Basic. Vira coluna flex p/ o botão ir para a base. */
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}
.desktop-card-col :deep(.card-body) {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}
.desktop-card-col :deep(.card-body > .row:last-child) {
  margin-top: auto;
}
/* Linhas de período com altura uniforme: o Plus tem a linha de créditos que
   aumenta a altura do item; o min-height faz o Basic (sem créditos) reservar o
   mesmo espaço, alinhando o NOME do período linha a linha entre os cards (o
   conteúdo flui do topo; os créditos do Plus ocupam o espaço reservado abaixo).
   Não afeta a célula bloqueada (que já tem altura própria). */
.desktop-card-col :deep(.card-body-item:not(.blocked)) {
  min-height: 100px;
  /* Centraliza verticalmente as informações do período dentro do elemento
     (cards sem créditos, como o Basic, deixavam o conteúdo colado no topo,
     muito perto do hr acima). */
  display: flex;
  flex-direction: column;
  justify-content: center;
}
/* Rádio + período + valor na MESMA linha (como no mobile). O col-1 (8.33%) é
   estreito demais para o rádio (24px) em card estreito, fazendo-o transbordar;
   fixamos a coluna do rádio e ajustamos a do conteúdo. */
.desktop-card-col :deep(.card-body-item > .row > .col-1) {
  flex: 0 0 30px;
  max-width: 30px;
  width: 30px;
}
.desktop-card-col :deep(.card-body-item > .row > .col-11) {
  flex: 0 0 calc(100% - 30px);
  max-width: calc(100% - 30px);
  width: calc(100% - 30px);
}
/* Botão do card: largura fluida (o width:202px fixo estoura card estreito). */
.desktop-card-col :deep(.plan-button),
.desktop-card-col :deep(.plan-button-inactive) {
  width: 90%;
  max-width: 220px;
}

/* Tabela de benefícios: rótulo de benefício (1ª coluna) + 3 colunas de plano. */
.grid-benefits {
  display: grid;
  grid-template-columns: 240px repeat(3, 1fr);
  gap: 0;
  align-items: stretch;
}

/* Tabela de benefícios: rótulo do benefício mantém respiro à esquerda. */
.grid-benefits > .benefit-name {
  padding-left: 10px;
}

/* Última linha do grid de benefícios (4 filhos finais = nome + 3 marcas): sem
   borda inferior, para fechar a tabela sem traço extra abaixo. */
.grid-benefits > *:nth-last-child(-n+4) {
  border-bottom: none;
}

/* Benefícios (lista V2) em formato de tabela: nome só na 1ª coluna; nas colunas
   dos planos, só check/X. row-gap 0 + células esticadas mantêm o cinza da
   coluna Plus contínuo entre as linhas e a partir do grid de planos. */
.grid-benefits {
  margin-top: 30px;
  row-gap: 0;
  align-items: stretch;
}

/* Nome do benefício (1ª coluna), alinhado à esquerda. */
.benefit-name {
  display: flex;
  align-items: center;
  padding: 8px 8px 8px 4px;
  font-family: "Exo-2";
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  color: #050505;
  text-align: left;
  border-bottom: 1px solid #b5b5b5;
}
.benefit-name-bold {
  font-weight: 700;
}

/* Marca (check/X) centralizada na coluna de cada plano. */
.benefit-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  border-bottom: 1px solid #b5b5b5;
}
.benefit-mark :deep(svg) {
  width: 20px;
  height: 20px;
}

/* Arredonda o canto inferior da coluna Plus no último benefício (espelha o topo). */
.grid-benefits .benefit-mark.plus-gray:nth-last-child(2) {
  /*border-radius: 0 0 12px 12px;*/
}

/* Rodapé. */
.grid-footer {
  margin: 30px;
}
.footer-secrecy {
  color: #5b5858;
  font-family: "Roboto-Regular";
  font-size: 25px;
  font-style: italic;
  line-height: 30px;
}
.footer-download {
  color: #5b5858;
  font-family: "Roboto-Regular";
  font-size: 13px;
  font-style: italic;
  line-height: 18px;
}

/* Abaixo de 1200px: a coluna do texto de chamada ("Tenha acesso a fotos…") é
   ocultada e a coluna de rótulo à esquerda encolhe (240px → 160px) TANTO na
   linha de cards QUANTO na tabela de benefícios, mantendo o alinhamento
   card ↔ check/X. A largura liberada vai para os 3 cards de planos, que se
   expandem. O componente só renderiza no desktop (!isMobile, >=770px), então
   este intervalo é efetivamente 770–1199px. */
@media screen and (max-width: 1199px) {
  /* Texto de chamada de marketing (visitante) some para dar largura aos cards.
     O cabeçalho do assinante ATIVO NÃO some: volta para o topo (largura total). */
  .desktop-intro:not(.desktop-intro--active) {
    display: none;
  }
  /* Sem a intro, fixa os cards na 2ª coluna (a 1ª, de 160px, fica vazia aqui e
     espelha a coluna de nomes dos benefícios para preservar o alinhamento). */
  .desktop-cards {
    grid-column: 2;
  }
  .desktop-plans-row {
    grid-template-columns: 160px 1fr;
  }
  .grid-benefits {
    grid-template-columns: 160px repeat(3, 1fr);
  }

  /* Assinante ativo: o cabeçalho ocupa a linha inteira acima dos cards (como
     antes da migração p/ a coluna). Os cards permanecem na 2ª coluna (offset de
     160px), alinhados à tabela de benefícios abaixo. */
  .desktop-plans-row--active-header .desktop-intro--active {
    grid-column: 1 / -1;
    padding-right: 0;
    margin-bottom: 24px;
  }
}

/* ===== Tema Gold (aba Renovar/Upgrade da Minha Conta) ===== */
/* A classe .gold-theme vem da raiz do NavBarAccount — em /assine ela não
   existe, então nada aqui muda naquela página. Os CARDS de plano continuam
   como estão (AssinePlanCardPeriodMobile não é tocado); aqui só entram os
   textos ao redor: chamadas, cabeçalhos de estado, benefícios e rodapé. */
.gold-theme .gold-top-subtitle,
.gold-theme .gold-congrats-title,
.gold-theme .gold-renew-title,
.gold-theme .active-upgrade-subtitle,
.gold-theme .desktop-intro-inactive-sub,
.gold-theme .desktop-intro-1,
.gold-theme .desktop-intro-2,
.gold-theme .desktop-intro-3 {
  color: #e9c554;
}

.gold-theme .gold-top-title,
.gold-theme .gold-top-text,
.gold-theme .gold-congrats-subtitle,
.gold-theme .gold-renew-subtitle,
.gold-theme .desktop-intro-inactive-title,
.gold-theme .desktop-intro-4,
.gold-theme .desktop-intro-5,
.gold-theme .benefit-name,
.gold-theme .footer-secrecy,
.gold-theme .footer-download {
  color: #ffffff;
}

/* X dos itens não inclusos: o vermelho #b20000 do SVG some no fundo escuro.
   O fill vem em atributo dentro do v-html, por isso o :deep no <g>. */
.gold-theme .benefit-mark--off :deep(svg g) {
  fill: #ffffff;
}

.gold-theme .benefit-name,
.gold-theme .benefit-mark {
  border-bottom-color: #3a3a3a;
}
</style>
